import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Pager, usePageParam } from "@/components/ui/pager";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/date-utils";
import { TEXT_LINK_CLASS } from "@/lib/linkStyles";
import { cn } from "@/lib/utils";
import { useGalleryDetail } from "@/queries/event-gallery";
import { GalleryLightbox } from "./GalleryLightbox";
import { PhotoUploadSection } from "./PhotoUploadSection";

/** Placeholder heading and tiles, shaped like the page they stand in for. */
function GalleryPageSkeleton() {
  return (
    <PageContainer>
      <div data-testid="gallery-page-skeleton" className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
            <Skeleton key={n} className="aspect-square w-full rounded-md" />
          ))}
        </div>
      </div>
    </PageContainer>
  );
}

/** Breadcrumb back to the gallery index, matching the event page's back link. */
function BackToGallery() {
  return (
    <Link
      to="/gallery"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
    >
      <ArrowLeft className="size-4" />
      Back to Gallery
    </Link>
  );
}

/**
 * One gallery's grid of approved photos with uploader attribution — the page
 * "See all" on an event's gallery preview leads to. Open to any signed-in
 * user, like the preview itself. Photos are paged by the `?page` URL param so
 * a page is linkable and survives refresh.
 */
export function GalleryPage() {
  const { galleryId } = useParams<{ galleryId: string }>();
  const [page, setPage] = usePageParam();
  const { data, isLoading, isPlaceholderData, error } = useGalleryDetail(
    galleryId!,
    page,
    !!galleryId,
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // A page past the end (a stale link, or photos removed since) comes back
  // empty; send the viewer to the last real page instead of an empty grid.
  const lastPage = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;
  const pastTheEnd = !!data && !isPlaceholderData && page > lastPage;
  useEffect(() => {
    if (pastTheEnd) setPage(lastPage, { replace: true });
  }, [pastTheEnd, lastPage, setPage]);

  if (isLoading) return <GalleryPageSkeleton />;

  if (error) {
    return (
      <PageContainer>
        <BackToGallery />
        <h1 className="text-2xl font-bold">Gallery Not Found</h1>
        <p className="text-muted-foreground">
          {error.data?.code === "NOT_FOUND" ? "This gallery could not be found." : error.message}
        </p>
      </PageContainer>
    );
  }

  if (!data) return null;

  // Rendered above and below the grid so a long page can be stepped from
  // either end.
  const pager = (
    <Pager
      page={page}
      perPage={data.per_page}
      total={data.total}
      onPageChange={(next) => {
        setPage(next);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
    />
  );

  return (
    <PageContainer>
      <BackToGallery />
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{data.title}</h1>
          {data.event && (
            <p className="space-x-3 text-sm">
              From the event:{" "}
              <Link to={`/events/${data.event.id}`} className={TEXT_LINK_CLASS}>
                {data.event.name}
              </Link>
              {data.event.start_date && (
                <span className="text-muted-foreground">
                  {formatDate(data.event.start_date)}
                  {data.event.end_date &&
                    data.event.end_date !== data.event.start_date &&
                    ` – ${formatDate(data.event.end_date)}`}
                </span>
              )}
            </p>
          )}
          {data.description && <p className="text-muted-foreground">{data.description}</p>}
        </div>

        {/* can_contribute is always false for event-less galleries, but the
            event guard also narrows the type — uploads need its id. */}
        {data.can_contribute && data.event && <PhotoUploadSection eventId={data.event.id} />}

        {pager}

        {data.total === 0 ? (
          <p className="text-muted-foreground">This gallery has no photos yet.</p>
        ) : (
          <div
            aria-busy={isPlaceholderData}
            className={cn(
              "grid grid-cols-2 gap-x-2 gap-y-4 transition-opacity sm:grid-cols-3 md:grid-cols-4",
              isPlaceholderData && "opacity-60",
            )}
          >
            {data.photos.map((photo, index) => (
              <figure key={photo.id} className="space-y-1">
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={`View photo ${index + 1}`}
                  className="block aspect-square w-full overflow-hidden rounded-md bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
                >
                  <img
                    src={photo.thumbnail_url}
                    alt=""
                    loading="lazy"
                    className="size-full object-cover transition-transform hover:scale-105"
                  />
                </button>
              </figure>
            ))}
          </div>
        )}

        {pager}

        {/* Gallery photos are approved by construction, so admins may edit
            tags here; the lightbox hides the toggle from everyone else. */}
        <GalleryLightbox
          photos={data.photos}
          index={activeIndex}
          onIndexChange={setActiveIndex}
          canEditTags
        />
      </div>
    </PageContainer>
  );
}
